import React, {useState, useMemo} from 'react'
import {Box, Link, Image, HStack, Text, useSlotRecipe, useRecipe} from '@chakra-ui/react3'
import DisImage from '../dis-image'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {PROMOTION_BADGE_MESSAGES} from '../../../constants'
import useProductSpecialOffers from '../../../hooks/use-product-special-offers'
import {ChevronDownIcon, ChevronUpIcon} from '../icons'
import Slider from '../cms/slider'
import StyledContent from '../styled-content'
import {promotionBannerRecipe, promotionItemRecipe, THEME_FOR_COLOR_VARIANT} from './index.recipe'
import {normalizeColorWorld} from '../../../utils/utils'
import {Chakra3Theme} from '../../theme'

const PromotionItem = ({promotion, isActive}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isIconURLValid, setIsIconURLValid] = useState(true)
    const [isImageURLValid, setIsImageURLValid] = useState(true)
    const {formatMessage} = useIntl()

    const [prevIsActive, setPrevIsActive] = useState(isActive)

    if (isActive !== prevIsActive) {
        setPrevIsActive(isActive)

        if (!isActive) {
            setIsOpen(false)
        }
    }

    const colorWorld = useMemo(
        () => normalizeColorWorld(promotion?.c_colorWorld),
        [promotion?.c_colorWorld]
    )

    // image and icon are mutually exclusive — image takes precedence
    const mediaType =
        promotion?.image && isImageURLValid
            ? 'image'
            : promotion?.c_promotionIcon && isIconURLValid
            ? 'icon'
            : null

    const hasDetails = !!promotion?.details

    const itemRecipe = useSlotRecipe({recipe: promotionItemRecipe})
    const styles = useMemo(
        () => itemRecipe({colorWorld, mediaType, hasDetails}),
        [itemRecipe, colorWorld, mediaType, hasDetails]
    )

    return (
        <Box css={styles.root}>
            {mediaType === 'image' && (
                <Box css={styles.imageBox}>
                    <DisImage
                        alt={promotion.name}
                        src={promotion.image}
                        css={styles.image}
                        widths={{base: '70px'}} // matches imageBox width of 4.375rem
                        onError={() => setIsImageURLValid(false)}
                    />
                </Box>
            )}
            <Box css={styles.textContainer}>
                <HStack css={styles.headingRow}>
                    {mediaType === 'icon' && (
                        <Image
                            alt={promotion.name}
                            src={promotion.c_promotionIcon}
                            css={styles.iconBox}
                            onError={() => setIsIconURLValid(false)}
                        />
                    )}
                    <Text css={styles.heading}>
                        {PROMOTION_BADGE_MESSAGES[promotion?.c_badge]
                            ? formatMessage(PROMOTION_BADGE_MESSAGES[promotion.c_badge])
                            : promotion.name}
                    </Text>
                </HStack>

                <Box css={styles.content}>
                    <StyledContent content={promotion?.calloutMsg} />
                </Box>

                {isOpen && promotion.details && (
                    <Box
                        ref={(el) => el?.focus()}
                        tabIndex="-1"
                        css={styles.content}
                        className="leap-container"
                    >
                        <StyledContent content={promotion?.details} />
                    </Box>
                )}

                {promotion.details && (
                    <Link
                        as="button"
                        variant="secondary"
                        underline
                        size="small"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                    >
                        {isOpen ? (
                            <FormattedMessage
                                id="leap.promotion.show_less"
                                defaultMessage="Show less"
                            />
                        ) : (
                            <FormattedMessage
                                id="leap.promotion.show_more"
                                defaultMessage="Show more"
                            />
                        )}
                        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </Link>
                )}
            </Box>
        </Box>
    )
}

PromotionItem.propTypes = {
    promotion: PropTypes.object,
    isActive: PropTypes.bool
}

const PromotionBanner = ({product}) => {
    const {formatMessage} = useIntl()
    const [activeIndex, setActiveIndex] = useState(0)

    const {promotions, loading} = useProductSpecialOffers(product, null, 'xpdp')

    const theme = useMemo(
        () =>
            THEME_FOR_COLOR_VARIANT[normalizeColorWorld(promotions?.[activeIndex]?.c_colorWorld)] ||
            THEME_FOR_COLOR_VARIANT.default,
        [promotions, activeIndex]
    )

    const bannerRecipe = useRecipe({recipe: promotionBannerRecipe})
    const bannerStyles = useMemo(() => bannerRecipe(), [bannerRecipe])

    if (loading || !promotions?.length) return null

    const isSingleItem = promotions.length === 1

    return (
        <Chakra3Theme theme={theme}>
            <Box
                as="section"
                aria-label={formatMessage({
                    id: 'leap.promotion.assistive_msg.section_label',
                    defaultMessage: 'promotion'
                })}
                css={bannerStyles}
            >
                {isSingleItem ? (
                    <PromotionItem promotion={promotions[0]} isActive />
                ) : (
                    <Slider
                        variant="promotionBanner"
                        sliderLabel="Product Promotions"
                        onIndexChange={(index) => setActiveIndex(index)}
                    >
                        {promotions.map((promo, idx) => (
                            <PromotionItem
                                key={promo.id}
                                promotion={promo}
                                isActive={activeIndex === idx}
                            />
                        ))}
                    </Slider>
                )}
            </Box>
        </Chakra3Theme>
    )
}

PromotionBanner.propTypes = {
    product: PropTypes.object
}

export default PromotionBanner
